import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Send, Star, ShieldCheck, Phone } from 'lucide-react'
import { PageHead } from '../AdminLayout'
import {
  Panel, DataTable, Pager, Toolbar, Select, SearchBox, RefreshButton, Status, Drawer, Tag,
} from '../ui'
import { get, post, patch, qs } from '../../lib/api'
import { toast } from '../../store'
import { fa, faDate, timeAgo, asArray } from '../../lib/format'

const PRIORITY_KIND: Record<string, 'down' | 'gold' | 'muted'> = {
  high: 'down', normal: 'muted', low: 'muted',
}

/* --------------------------------------------------- گفتگوی تیکت */
function Thread({ id, onChanged, onClose }: { id: number; onChanged: () => void; onClose: () => void }) {
  const [data, setData] = useState<any>(null)
  const [answer, setAnswer] = useState('')
  const [sending, setSending] = useState(false)
  const endRef = useRef<HTMLDivElement>(null)

  const load = useCallback(() => {
    get(`/admin/content/tickets/${id}`)
      .then(setData)
      .catch((e: any) => { toast.err(e.message); onClose() })
  }, [id, onClose])

  useEffect(() => { setData(null); load() }, [id, load])
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }) }, [data?.messages?.length])

  const reply = async (close: boolean) => {
    if (answer.trim().length < 3) return toast.err('متن پاسخ کوتاه است')
    setSending(true)
    try {
      await post(`/admin/content/tickets/${id}/reply`, { answer, close })
      toast.ok(close ? 'پاسخ ارسال و تیکت بسته شد' : 'پاسخ ارسال شد')
      setAnswer('')
      load()
      onChanged()
    } catch (e: any) {
      toast.err(e.message)
    } finally {
      setSending(false)
    }
  }

  const change = async (values: any) => {
    try {
      await patch(`/admin/content/tickets/${id}`, values)
      load()
      onChanged()
    } catch (e: any) { toast.err(e.message) }
  }

  if (!data) return <div className="py-10 text-center text-xs text-slate-500">در حال بارگذاری…</div>

  const messages = asArray<any>(data.messages)

  return (
    <>
      {/* اطلاعات کاربر — کارشناس باید پیش از پاسخ بداند با چه کسی طرف است */}
      <div className="mb-4 rounded-2xl border border-white/[.07] bg-ink-950/60 p-4">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
          <Link to={`/admin/users/${data.userId}`} className="num text-brand-300 hover:underline" dir="ltr">
            {data.email}
          </Link>
          {data.fullName && <span className="text-slate-300">{data.fullName}</span>}
          {data.mobile && (
            <span className="num inline-flex items-center gap-1 text-slate-500" dir="ltr">
              <Phone className="h-3 w-3" /> {data.mobile}
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-slate-500">
            <ShieldCheck className="h-3 w-3" /> سطح {fa(data.kycLevel || 0)}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
          <span>تیکت #{fa(data.id)}</span>
          <span>·</span>
          <span>{data.department}</span>
          <span>·</span>
          <span>ثبت {faDate(data.createdAt)}</span>
          <Status value={data.status} />
          <Tag kind={PRIORITY_KIND[data.priority] || 'muted'}>{data.priorityLabel}</Tag>
          {data.rating ? (
            <span className="inline-flex items-center gap-1 text-gold">
              <Star className="h-3 w-3 fill-gold" /> {fa(data.rating)}
            </span>
          ) : null}
        </div>
      </div>

      {/* گفتگو */}
      <div className="mb-4 max-h-[45vh] space-y-3 overflow-y-auto pl-1">
        {messages.map((m: any, i: number) => (
          <div key={m.id ?? i} className={`flex ${m.isStaff ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl border p-3 ${
              m.isStaff ? 'border-brand-500/25 bg-brand-500/10' : 'border-white/[.07] bg-white/[.03]'
            }`}>
              <div className="mb-1.5 flex items-center gap-2 text-[11px]">
                <span className={m.isStaff ? 'font-semibold text-brand-200' : 'font-semibold text-slate-300'}>
                  {m.isStaff ? 'پشتیبانی' : (m.author || 'کاربر')}
                </span>
                <span className="text-slate-500">{timeAgo(m.createdAt)}</span>
              </div>
              <p className="whitespace-pre-wrap text-sm leading-7 text-slate-200">{m.body}</p>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {/* تغییر اولویت و وضعیت، بدون ارسال پاسخ */}
      <div className="mb-3 flex flex-wrap gap-2">
        <Select
          value={data.priority}
          onChange={(v) => change({ priority: v })}
          options={[['low', 'اولویت: کم'], ['normal', 'اولویت: عادی'], ['high', 'اولویت: فوری']]}
        />
        <Select
          value={data.status}
          onChange={(v) => change({ status: v })}
          options={[['open', 'باز'], ['pending', 'در حال بررسی'],
                    ['answered', 'پاسخ داده شد'], ['closed', 'بسته']]}
        />
      </div>

      <label className="label">پاسخ شما</label>
      <textarea className="input mb-3 min-h-[140px]" value={answer}
                onChange={(e) => setAnswer(e.target.value)} placeholder="پاسخ خود را بنویسید…" />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => reply(false)} disabled={sending} className="btn-primary flex-1 !py-3">
          <Send className="h-4 w-4" /> ارسال پاسخ
        </button>
        <button onClick={() => reply(true)} disabled={sending} className="btn-ghost !py-3 text-xs">
          ارسال و بستن تیکت
        </button>
      </div>
    </>
  )
}

/* --------------------------------------------------- صفحه */
export default function Tickets() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [meta, setMeta] = useState<any>(null)
  const [departments, setDepartments] = useState<string[]>([])
  const [openCount, setOpenCount] = useState(0)
  const [status, setStatus] = useState('open')
  const [department, setDepartment] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState<any>(null)

  const load = () => {
    setLoading(true)
    get(`/admin/content/tickets${qs({ status, department, q, page, per_page: 25 })}`)
      .then((r: any) => {
        setRows(asArray(r, 'data'))
        setMeta(r.meta)
        setDepartments(asArray<string>(r.departments))
        setOpenCount(r.openCount || 0)
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }
  useEffect(load, [status, department, q, page])

  return (
    <>
      <PageHead
        title="تیکت‌های پشتیبانی"
        subtitle={openCount ? `${fa(openCount)} تیکت باز در انتظار رسیدگی` : 'پاسخ به درخواست‌های کاربران'}
      />
      <Panel dense>
        <div className="p-4 pb-0">
          <Toolbar>
            <Select
              value={status}
              onChange={(v) => { setStatus(v); setPage(1) }}
              options={[['open', 'باز (در جریان)'], ['answered', 'پاسخ داده شده'],
                        ['pending', 'در حال بررسی'], ['closed', 'بسته'], ['', 'همه']]}
            />
            <Select
              value={department}
              onChange={(v) => { setDepartment(v); setPage(1) }}
              options={[['', 'همه دپارتمان‌ها'], ...departments.map((d) => [d, d] as [string, string])]}
            />
            <SearchBox value={q} onChange={(v) => { setQ(v); setPage(1) }} placeholder="جستجوی موضوع…" />
            <RefreshButton onClick={load} loading={loading} />
          </Toolbar>
        </div>

        <DataTable
          rows={rows} loading={loading} minWidth={880} empty="تیکتی وجود ندارد"
          onRowClick={(r: any) => setView(r)}
          columns={[
            {
              key: 'id', header: '#',
              cell: (r: any) => (
                <span className="num flex items-center gap-1.5 text-slate-500">
                  {r.unreadStaff > 0 && <span className="h-2 w-2 rounded-full bg-down" title="پیام خوانده‌نشده" />}
                  {fa(r.id)}
                </span>
              ),
            },
            {
              key: 'u', header: 'کاربر',
              cell: (r: any) => (
                <Link to={`/admin/users/${r.userId}`} className="num text-brand-300 hover:underline" dir="ltr">
                  {r.email}
                </Link>
              ),
            },
            { key: 's', header: 'موضوع', cell: (r: any) => <span className="text-slate-100">{r.subject}</span> },
            { key: 'de', header: 'بخش', cell: (r: any) => <span className="text-slate-400">{r.department}</span> },
            {
              key: 'p', header: 'اولویت',
              cell: (r: any) => <Tag kind={PRIORITY_KIND[r.priority] || 'muted'}>{r.priorityLabel}</Tag>,
            },
            { key: 'st', header: 'وضعیت', cell: (r: any) => <Status value={r.status} /> },
            {
              key: 'd', header: 'آخرین پیام',
              cell: (r: any) => <span className="text-slate-500">{timeAgo(r.lastReplyAt)}</span>,
            },
          ]}
        />
        <Pager meta={meta} onPage={setPage} />
      </Panel>

      <Drawer open={!!view} onClose={() => setView(null)} title={view?.subject ?? ''} wide>
        {view && <Thread id={view.id} onChanged={load} onClose={() => setView(null)} />}
      </Drawer>
    </>
  )
}
